/**
 * BYOM — Admin → Local LLM tab (FE1).
 *
 * CRUD UI for local_llm_endpoints rows: register a self-hosted OpenAI-compatible
 * endpoint (Ollama / vLLM / LM Studio / TGI / NIM), test it (chat + embed probe),
 * inspect its models, and delete it (blocked with a clear message when still
 * referenced by an LLM ranking). Secrets (api_key, client key) are never returned
 * by the API — the UI shows set/unset state and lets the admin rotate.
 *
 * Backend: app/routers/local_llm_endpoints.py (require_administrator).
 */
import React, { useEffect, useState } from "react";
import { COLORS } from "../../theme/colors";
import { Button, Badge, Card, Input, Select, Modal } from "../../components/ui";
import { localLlmApi } from "../../api/client";
import ModelServingLight from "./ModelServingLight";

// Exported for unit testing
export const HEALTH_COLORS = {
  healthy: "#22c55e",
  degraded: "#f59e0b",
  unreachable: "#ef4444",
  unknown: COLORS.textMuted || "#94a3b8",
};

// The per-row health indicator. On-demand models scale to zero, so a generic
// health badge is misleading (stale-"healthy" while parked, or false-"unhealthy"
// since a parked model isn't broken) — show the LIVE serving light instead;
// always-on hosts keep the badge. Extracted from the row JSX so each branch is a
// plain function-body `return` (v8 credits those reliably, unlike a ternary
// inside a JSX expression container, which the parallel coverage merge under-counts).
export function EndpointHealthCell({ row }) {
  if (row.is_on_demand) return <ModelServingLight endpointId={row.endpoint_id} />;
  const color = HEALTH_COLORS[row.last_health_status] || HEALTH_COLORS.unknown;
  return <Badge color={color}>{row.last_health_status || "unknown"}</Badge>;
}

// Exported for unit testing — build the create-endpoint request body. Coerce
// the timeout to a number (default 120) and drop blank optionals so the backend
// keeps its own defaults rather than receiving empty strings.
export function buildCreateBody(form) {
  const body = { ...form, timeout_seconds: Number(form.timeout_seconds) || 120 };
  if (!body.api_key) delete body.api_key;
  if (!body.default_model) delete body.default_model;
  return body;
}

// Exported for unit testing — a test probe is "ok" only when the response
// envelope didn't fail (success !== false) AND the inner probe reports ok.
export function computeTestOk(res) {
  return res?.success !== false && !!res?.data?.ok;
}

// Exported for unit testing — turn a delete failure into operator-facing copy.
// A 409 carries body.detail.rankings listing the ranking entries still
// referencing the endpoint; anything else falls back to the raw message.
export function deleteErrorMessage(e) {
  const refs = e?.body?.detail?.rankings;
  if (refs?.length) {
    return `Still referenced by ${refs.length} ranking entr${refs.length === 1 ? "y" : "ies"}. Remove it from Admin → LLM Rankings first.`;
  }
  return e?.message || "Delete failed";
}

const EMPTY_FORM = {
  endpoint_id: "",
  display_name: "",
  provider_class: "local_openai_compat",
  base_url: "",
  api_key: "",
  default_model: "",
  timeout_seconds: 120,
  verify_ssl: true,
  enabled: true,
};

export default function LocalLlmEndpointsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerClasses, setProviderClasses] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [testing, setTesting] = useState(null); // endpoint_id being tested
  const [testResult, setTestResult] = useState(null); // { endpoint_id, ok, detail }
  const [confirmDelete, setConfirmDelete] = useState(null); // row or null
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await localLlmApi.list();
      setRows(res?.data || []);
    } catch (e) {
      console.error("local endpoints load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    localLlmApi
      .providerClasses()
      .then((r) => setProviderClasses((r?.data || []).filter((c) => c.visible_in_admin)))
      .catch(() => setProviderClasses([{ key: "local_openai_compat", display_name: "OpenAI-compatible" }]));
  }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormErr("");
    setShowCreate(true);
  };

  const submitCreate = async () => {
    setSaving(true);
    setFormErr("");
    try {
      // Drop empty optionals so the backend keeps its defaults.
      const body = buildCreateBody(form);
      await localLlmApi.create(body);
      setShowCreate(false);
      await refresh();
    } catch (e) {
      setFormErr(e?.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (row) => {
    setTesting(row.endpoint_id);
    setTestResult(null);
    try {
      const res = await localLlmApi.test(row.endpoint_id);
      // {success:false, error:{...}} on build failure; {success:true, data:{ok,...}}
      const ok = computeTestOk(res);
      setTestResult({ endpoint_id: row.endpoint_id, ok, detail: res?.data || res?.error });
    } catch (e) {
      setTestResult({ endpoint_id: row.endpoint_id, ok: false, detail: { error: e?.message } });
    } finally {
      setTesting(null);
      await refresh(); // health/status persisted server-side
    }
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    setDeleteErr("");
    try {
      await localLlmApi.remove(confirmDelete.endpoint_id);
      setConfirmDelete(null);
      await refresh();
    } catch (e) {
      // 409: still referenced by a ranking — body.detail.rankings lists refs.
      setDeleteErr(deleteErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h3 style={{ margin: 0, color: COLORS.text }}>Local LLM Endpoints</h3>
          <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 4 }}>
            Self-hosted OpenAI-compatible endpoints (Ollama · vLLM · LM Studio · TGI · NIM).
            Register one, then pick its models in <strong>LLM Rankings</strong>. Data stays in
            your own stack.
          </div>
        </div>
        <Button onClick={openCreate} data-testid="local-llm-add">+ Add Endpoint</Button>
      </div>

      {loading ? (
        <div style={{ color: COLORS.textMuted }}>Loading…</div>
      ) : rows.length === 0 ? (
        <Card>
          <div style={{ color: COLORS.textMuted, textAlign: "center", padding: "24px 0" }}>
            No local endpoints yet. Add one to route chat / voice / embeddings to a self-hosted model.
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((row) => (
            <Card key={row.endpoint_id} data-testid={`local-llm-row-${row.endpoint_id}`} style={{ opacity: row.enabled ? 1 : 0.6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ minWidth: 220 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <strong style={{ color: COLORS.text }}>{row.display_name}</strong>
                    <EndpointHealthCell row={row} />
                    {!row.enabled && <Badge color={COLORS.textMuted}>disabled</Badge>}
                    {row.mtls_configured && <Badge color="#8b5cf6">mTLS</Badge>}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
                    <code>{row.endpoint_id}</code> · {row.base_url}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
                    default: {row.default_model || "(none)"} · models cached:{" "}
                    {(row.cached_models || []).length} · key:{" "}
                    {row.api_key_set ? "set" : "none"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <Button
                    variant="secondary"
                    data-testid={`local-llm-test-${row.endpoint_id}`}
                    onClick={() => runTest(row)}
                    disabled={testing === row.endpoint_id}
                  >
                    {testing === row.endpoint_id ? "Testing…" : "Test"}
                  </Button>
                  <Button
                    variant="secondary"
                    data-testid={`local-llm-delete-${row.endpoint_id}`}
                    onClick={() => {
                      setConfirmDelete(row);
                      setDeleteErr("");
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>

              {testResult?.endpoint_id === row.endpoint_id && (
                <div
                  data-testid={`local-llm-test-result-${row.endpoint_id}`}
                  style={{
                    marginTop: 10,
                    padding: "8px 10px",
                    borderRadius: 6,
                    fontSize: 12,
                    background: testResult.ok ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                    color: testResult.ok ? "#16a34a" : "#dc2626",
                  }}
                >
                  {testResult.ok ? "✓ Reachable" : "✗ Failed"}
                  {testResult.detail?.latency_ms != null && ` · ${testResult.detail.latency_ms}ms`}
                  {testResult.detail?.models_count != null &&
                    ` · ${testResult.detail.models_count} models`}
                  {testResult.detail?.embed_probe &&
                    ` · embed ${testResult.detail.embed_probe.ok ? "✓" : "✗"}`}
                  {testResult.detail?.error && ` · ${testResult.detail.error}`}
                  {testResult.detail?.error_class && ` (${testResult.detail.error_class})`}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create modal */}
      <Modal
        open={showCreate}
        onClose={() => !saving && setShowCreate(false)}
        title="Add Local LLM Endpoint"
        width={520}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="secondary" onClick={() => setShowCreate(false)} disabled={saving}>
              Cancel
            </Button>
            <Button data-testid="local-llm-create" onClick={submitCreate} disabled={saving || !form.endpoint_id || !form.display_name || !form.base_url}>
              {saving ? "Saving…" : "Create"}
            </Button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {formErr && (
            <div style={{ color: "#dc2626", fontSize: 13, background: "rgba(239,68,68,0.1)", padding: "8px 10px", borderRadius: 6 }}>
              {formErr}
            </div>
          )}
          <Input
            label="Endpoint ID (slug)"
            helper="Lowercase a-z0-9 _ -, e.g. ollama-lan"
            data-testid="local-llm-f-endpoint-id"
            value={form.endpoint_id}
            onChange={(e) => setForm({ ...form, endpoint_id: e.target.value })}
            placeholder="ollama-lan"
          />
          <Input
            label="Display name"
            data-testid="local-llm-f-display-name"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            placeholder="Ollama (LAN box)"
          />
          <Select
            label="Provider class"
            value={form.provider_class}
            onChange={(e) => setForm({ ...form, provider_class: e.target.value })}
          >
            {providerClasses.map((c) => (
              <option key={c.key} value={c.key}>
                {c.display_name}
              </option>
            ))}
          </Select>
          <Input
            label="Base URL"
            helper="OpenAI-compatible /v1 base, e.g. http://ollama:11434/v1"
            data-testid="local-llm-f-base-url"
            value={form.base_url}
            onChange={(e) => setForm({ ...form, base_url: e.target.value })}
            placeholder="http://ollama:11434/v1"
          />
          <Input
            label="API key (optional)"
            type="password"
            helper="Blank for auth-less local servers (Ollama)."
            value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            placeholder="sk-… (optional)"
          />
          <Input
            label="Default model (optional)"
            data-testid="local-llm-f-default-model"
            value={form.default_model}
            onChange={(e) => setForm({ ...form, default_model: e.target.value })}
            placeholder="llama3.1:8b"
          />
          <Input
            label="Timeout (seconds)"
            type="number"
            value={form.timeout_seconds}
            onChange={(e) => setForm({ ...form, timeout_seconds: e.target.value })}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: COLORS.text }}>
            <input
              type="checkbox"
              checked={form.verify_ssl}
              onChange={(e) => setForm({ ...form, verify_ssl: e.target.checked })}
            />
            Verify TLS certificate
          </label>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={!!confirmDelete}
        onClose={() => !deleting && setConfirmDelete(null)}
        title="Delete endpoint?"
        width={440}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button data-testid="local-llm-delete-confirm" onClick={performDelete} disabled={deleting} style={{ background: "#dc2626" }}>
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </div>
        }
      >
        <div style={{ fontSize: 14, color: COLORS.text }}>
          Permanently delete <strong>{confirmDelete?.display_name}</strong> (
          <code>{confirmDelete?.endpoint_id}</code>)? Agents pointing at this endpoint will fail
          until re-pointed.
        </div>
        {deleteErr && (
          <div style={{ marginTop: 10, color: "#dc2626", fontSize: 13, background: "rgba(239,68,68,0.1)", padding: "8px 10px", borderRadius: 6 }}>
            {deleteErr}
          </div>
        )}
      </Modal>
    </div>
  );
}
